import { Alert, MonetaryAmount } from '@components'
import { getDocumentsPageUrl, getSettingsPageUrl } from '@constants'
import { useBusinessContext } from '@context'
import type { IBusiness, EuVatContactJson } from '@query'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FaExclamationTriangle } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import {
  ColumnHeaderRow,
  EuCountryCode,
  EuNameCell,
  EuRowSpacer,
  EuSummaryBlock,
  EuSummaryCard,
  EuSummaryLabel,
  EuSummaryValue,
  FootNote,
  ReportBodyScroll,
  ReportCard,
  ReportNoticeBanner,
  ReportRow,
  ReportViewport,
  STALE_FIGURES_CLASS,
  ValueCell,
  ValueHeader,
  WarnMark
} from './interactive.styled'
import { euSalesTableMinWidthPx } from './periodUtils'
import { ReportTableFrame } from './ReportTableFrame'

type Props = {
  data: EuVatContactJson
  business?: IBusiness | null
  periodLabel?: string
  currency?: string
  isStale?: boolean
}

const EU_COUNTRY_COLUMN_WIDTH = '140px'
const EU_NAME_COLUMN_WIDTH = '220px'
const EU_VAT_COLUMN_WIDTH = '180px'
const EU_SUPPLY_COLUMN_WIDTH = '160px'

const formatPeriod = (dateFrom?: string | null, dateTo?: string | null) => {
  if (!dateFrom || !dateTo) return ''
  return `${dayjs(dateFrom).format('D.M.YYYY')} – ${dayjs(dateTo).format('D.M.YYYY')}`
}

export const EuSalesView: React.FC<Props> = ({
  data,
  business,
  periodLabel,
  currency,
  isStale = false
}) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const total = useMemo(
    () => data.rows.reduce((sum, row) => sum + (row.net_amount || 0), 0),
    [data.rows]
  )
  const privatePersonRows = data.private_person_rows ?? []
  const attentionCount = useMemo(
    () => data.rows.filter(row => row.vat_id_missing || row.contact_missing).length,
    [data.rows]
  )

  const businessVatId = business?.invoicing_tax_code?.trim() || ''
  const businessVatMissing = !businessVatId
  const displayedBusinessVat = businessVatMissing ? data.business_vat_id : businessVatId
  const resolvedPeriod =
    periodLabel || formatPeriod(data.date_from, data.date_to) || t('reports.interactive.euEmpty')

  const postalLine = [business?.business_postal_code, business?.business_city]
    .filter(Boolean)
    .join(' ')

  return (
    <ReportViewport>
      <EuSummaryCard>
        <EuSummaryBlock>
          <EuSummaryLabel>{t('reports.interactive.companyDetails')}</EuSummaryLabel>
          <EuSummaryValue>{business?.name || '—'}</EuSummaryValue>
          {business?.business_street ? (
            <EuSummaryValue $muted>{business.business_street}</EuSummaryValue>
          ) : null}
          {postalLine ? <EuSummaryValue $muted>{postalLine}</EuSummaryValue> : null}
        </EuSummaryBlock>

        <EuSummaryBlock>
          <EuSummaryLabel>{t('reports.interactive.euVatNumber')}</EuSummaryLabel>
          {businessVatMissing ? (
            <>
              <WarnMark>
                <FaExclamationTriangle />
                {t('reports.interactive.euBusinessVatMissing')}
              </WarnMark>
              <SettingsLink to={getSettingsPageUrl(slug, { modal: 'business-info' })}>
                {t('reports.interactive.euAddInSettings')}
              </SettingsLink>
            </>
          ) : (
            <EuSummaryValue>{displayedBusinessVat}</EuSummaryValue>
          )}
        </EuSummaryBlock>

        <EuSummaryBlock>
          <EuSummaryLabel>{t('reports.interactive.period')}</EuSummaryLabel>
          <EuSummaryValue>{resolvedPeriod}</EuSummaryValue>
        </EuSummaryBlock>
      </EuSummaryCard>

      <ReportCard>
        {attentionCount > 0 ? (
          <ReportNoticeBanner $tone="warning">
            <Alert
              type="warning"
              inline
              marginBottom={0}
              description={
                <Trans
                  i18nKey="reports.interactive.euRowsNeedAttention"
                  count={attentionCount}
                  values={{ count: attentionCount }}
                >
                  <strong />
                </Trans>
              }
            />
          </ReportNoticeBanner>
        ) : null}
        {privatePersonRows.length > 0 ? (
          <ReportNoticeBanner $tone="warning">
            <Alert
              type="warning"
              inline
              marginBottom={0}
              description={
                <PrivatePersonNotice>
                  <span>{t('reports.interactive.euPrivatePersonNotice')}</span>
                  {privatePersonRows.map((row, index) => (
                    <PrivatePersonLine key={`${row.document_id ?? row.name}-${index}`}>
                      {row.document_id ? (
                        <DocumentLink to={getDocumentsPageUrl(slug, { id: row.document_id })}>
                          {row.document_number ? `${row.document_number} ` : null}
                          {row.name}
                        </DocumentLink>
                      ) : (
                        <span>{row.name}</span>
                      )}
                      <MonetaryAmount value={row.net_amount} currency={currency} />
                    </PrivatePersonLine>
                  ))}
                </PrivatePersonNotice>
              }
            />
          </ReportNoticeBanner>
        ) : null}
        <ReportBodyScroll className={isStale ? STALE_FIGURES_CLASS : undefined}>
          <ReportTableFrame minWidth={euSalesTableMinWidthPx()}>
            <ColumnHeaderRow>
              <ValueHeader $width={EU_COUNTRY_COLUMN_WIDTH}>
                {t('reports.interactive.euCountry')}
              </ValueHeader>
              <ValueHeader $width={EU_NAME_COLUMN_WIDTH}>
                {t('reports.interactive.euName')}
              </ValueHeader>
              <ValueHeader $width={EU_VAT_COLUMN_WIDTH}>
                {t('reports.interactive.euVatId')}
              </ValueHeader>
              <EuRowSpacer />
              <ValueHeader $width={EU_SUPPLY_COLUMN_WIDTH}>
                {t('reports.interactive.typeOfSupply')}
              </ValueHeader>
              <ValueHeader>{t('reports.interactive.amount')}</ValueHeader>
            </ColumnHeaderRow>

            {data.rows.length === 0 && privatePersonRows.length === 0 ? (
              <ReportRow>
                <EuCountryCode />
                <EuNameCell style={{ color: '#7b8794' }}>
                  {t('reports.interactive.euEmpty')}
                </EuNameCell>
                <ValueCell $width={EU_VAT_COLUMN_WIDTH} />
                <EuRowSpacer />
                <ValueCell $width={EU_SUPPLY_COLUMN_WIDTH} />
                <ValueCell />
              </ReportRow>
            ) : (
              data.rows.map((row, index) => {
                const vatMissing = row.vat_id_missing
                const contactMissing = row.contact_missing
                const rowWarn = vatMissing || contactMissing
                return (
                  <ReportRow
                    key={`${row.country_code}-${row.vat_id}-${row.name}-${index}`}
                    $warn={rowWarn}
                  >
                    <EuCountryCode title={row.country_code}>{row.country_code}</EuCountryCode>
                    <EuNameCell $semibold $warn={contactMissing}>
                      {contactMissing ? (
                        <WarnMark>
                          <FaExclamationTriangle />
                          {row.document_id ? (
                            <DocumentLink to={getDocumentsPageUrl(slug, { id: row.document_id })}>
                              {t('reports.interactive.euNoBuyerOnDocument', {
                                number: row.document_number || row.document_id
                              })}
                            </DocumentLink>
                          ) : (
                            t('reports.interactive.euNoBuyer')
                          )}
                        </WarnMark>
                      ) : (
                        row.name
                      )}
                    </EuNameCell>
                    <ValueCell
                      $width={EU_VAT_COLUMN_WIDTH}
                      $muted={!vatMissing}
                      $warn={vatMissing}
                      $semibold={vatMissing}
                    >
                      {vatMissing ? (
                        <WarnMark>
                          <FaExclamationTriangle />
                          {t('reports.interactive.euVatMissing')}
                        </WarnMark>
                      ) : (
                        row.vat_id
                      )}
                    </ValueCell>
                    <EuRowSpacer />
                    <ValueCell $width={EU_SUPPLY_COLUMN_WIDTH} $muted>
                      {row.vat_handling}
                    </ValueCell>
                    <ValueCell $strong>
                      <MonetaryAmount value={row.net_amount} currency={currency} />
                    </ValueCell>
                  </ReportRow>
                )
              })
            )}

            <ReportRow $strong $bg="#f7fafd" $borderStrong>
              <EuCountryCode />
              <EuNameCell $strong>{t('reports.interactive.euTotal')}</EuNameCell>
              <ValueCell $width={EU_VAT_COLUMN_WIDTH} />
              <EuRowSpacer />
              <ValueCell $width={EU_SUPPLY_COLUMN_WIDTH} />
              <ValueCell $strong>
                <MonetaryAmount value={total} currency={currency} />
              </ValueCell>
            </ReportRow>
          </ReportTableFrame>
        </ReportBodyScroll>
        <FootNote>{t('reports.interactive.footNote.eu')}</FootNote>
      </ReportCard>
    </ReportViewport>
  )
}

const SettingsLink = styled(Link)`
  display: block;
  margin-top: 5px;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`

const DocumentLink = styled(Link)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  overflow: hidden;
  text-overflow: ellipsis;
`

const PrivatePersonNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const PrivatePersonLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
